import { Component, EventEmitter, Input, Output, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoboothService } from '../../services/photobooth.service';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { LogDownload, resolveLogDownload } from './log-download';

@Component({
  selector: 'app-device-control-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './device-control-modal.component.html',
  styleUrls: ['./device-control-modal.component.css']
})
export class DeviceControlModalComponent implements OnInit {
  @Input() deviceId!: string;
  @Input() deviceName!: string;
  @Output() close = new EventEmitter<void>();

  private photoboothService = inject(PhotoboothService);
  private firestore = inject(Firestore);
  isSending = false;
  commandStatus: string = '';
  logDownload: LogDownload | null = null;

  // Schedule Properties
  autoRestartTime: string = '03:00';
  nightlyRestartEnabled: boolean = false;
  isSavingSchedule = false;
  scheduleStatus = '';

  // Log Date Prompt Properties
  showDateDialog: boolean = false;
  pendingLogCommand: string = '';
  pendingLogTitle: string = '';
  selectedLogDate: string = this.getTodayDateString();

  getTodayDateString(): string {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getYesterdayDateString(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setLogDateToday() {
    this.selectedLogDate = this.getTodayDateString();
  }

  setLogDateYesterday() {
    this.selectedLogDate = this.getYesterdayDateString();
  }

  promptForLogDate(command: string, label: string) {
    this.pendingLogCommand = command;
    this.pendingLogTitle = label;
    this.selectedLogDate = this.getTodayDateString();
    this.showDateDialog = true;
  }

  cancelDatePrompt() {
    this.showDateDialog = false;
    this.pendingLogCommand = '';
    this.pendingLogTitle = '';
  }

  confirmDateAndFetchLog() {
    if (!this.selectedLogDate) return;
    const cmd = this.pendingLogCommand;
    const date = this.selectedLogDate;
    this.showDateDialog = false;
    this.sendCommand(cmd, date);
  }

  async ngOnInit() {
    if (this.deviceId) {
      try {
        const deviceRef = doc(this.firestore, 'devices', this.deviceId);
        const snap = await getDoc(deviceRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data['autoRestartTime']) this.autoRestartTime = data['autoRestartTime'];
          if (data['nightlyRestartEnabled'] !== undefined) this.nightlyRestartEnabled = data['nightlyRestartEnabled'];
        }
      } catch (e) {
        console.error('Failed to load existing schedule', e);
      }
    }
  }

  commands = [
    { label: 'Restart App', action: 'RESTART_APP' },
    { label: 'Restart PC', action: 'RESTART_PC' },
    { label: 'Show Maintenance Screen', action: 'SHOW_MAINTENANCE' },
    { label: 'Hide Maintenance Screen', action: 'HIDE_MAINTENANCE' },
    { label: 'Force Sync / Refresh Config', action: 'FORCE_SYNC' },
    { label: 'View AppLog', action: 'UPLOAD_LOGS' },
    { label: 'View POSLog', action: 'UPLOAD_POS_LOGS' },
    { label: 'Clear Local Cache', action: 'CLEAR_CACHE' }
  ];

  onCommandClicked(command: string, label: string) {
    if (command === 'UPLOAD_LOGS' || command === 'UPLOAD_POS_LOGS') {
      this.promptForLogDate(command, label);
      return;
    }
    this.sendCommand(command);
  }

  async sendCommand(command: string, targetDate?: string) {
    if (!this.deviceId) return;

    if (command === 'RESTART_APP') {
      if (!confirm('Are you sure you want to restart the application on the kiosk? This will disrupt any active customer sessions.')) {
        return;
      }
    } else if (command === 'RESTART_PC') {
      if (!confirm('Are you sure you want to completely restart the Windows PC? The kiosk will go offline for a few minutes.')) {
        return;
      }
    }

    this.isSending = true;
    this.logDownload = null;
    this.commandStatus = targetDate
      ? `Requesting ${command === 'UPLOAD_LOGS' ? 'AppLog' : 'POSLog'} for ${targetDate} from kiosk...`
      : 'Sending command...';

    try {
      const payload: any = {
        reason: command === 'SHOW_MAINTENANCE'
          ? 'Manual trigger by admin via Device Control modal'
          : (command === 'HIDE_MAINTENANCE' 
              ? 'Manual restore by admin via Device Control modal' 
              : (targetDate ? `Admin requested ${command} for date ${targetDate}` : `Admin executed: ${command}`)),
        source: 'ERP_ADMIN_MANUAL'
      };

      if (targetDate) {
        payload.date = targetDate;
        payload.targetDate = targetDate;
      }

      await this.photoboothService.sendRemoteCommand(this.deviceId, command, payload);
      this.commandStatus = targetDate
        ? `Command sent for date ${targetDate}. Waiting for kiosk...`
        : 'Command sent. Waiting for kiosk...';
      
      // Listen for status
      const unsub = this.photoboothService.listenToRemoteCommandStatus(this.deviceId, (data) => {
        if (data.status === 'completed') {
          this.logDownload = resolveLogDownload(command, data);
          const fileInfo = this.logDownload?.isZip
            ? ` (${this.logDownload.fileCount} log file${this.logDownload.fileCount === 1 ? '' : 's'} zipped)`
            : (data.fileName ? ` (${data.fileName})` : '');
          this.commandStatus = `Command completed successfully!${fileInfo}`;
          this.isSending = false;
          unsub();

          if (this.logDownload) {
            window.open(this.logDownload.url, '_blank');
          }
        } else if (data.status === 'queued') {
          this.commandStatus = 'Command queued. Kiosk is currently in an active customer session and will execute once idle.';
        } else if (data.status === 'failed') {
          this.commandStatus = 'Command failed: ' + (data.error || 'Unknown error');
          this.isSending = false;
          unsub();
        }
      });
    } catch (e: any) {
      this.commandStatus = 'Error: ' + e.message;
      this.isSending = false;
    }
  }

  async saveSchedule() {
    if (!this.deviceId) return;
    this.isSavingSchedule = true;
    this.scheduleStatus = 'Saving...';
    try {
      const deviceRef = doc(this.firestore, 'devices', this.deviceId);
      await setDoc(deviceRef, {
        autoRestartTime: this.autoRestartTime,
        nightlyRestartEnabled: this.nightlyRestartEnabled
      }, { merge: true });

      await this.photoboothService.sendRemoteCommand(this.deviceId, 'SET_RESTART_SCHEDULE', {
        autoRestartTime: this.autoRestartTime,
        nightlyRestartEnabled: this.nightlyRestartEnabled
      });

      this.scheduleStatus = 'Schedule saved and sent to kiosk!';
      setTimeout(() => this.scheduleStatus = '', 3000);
    } catch (e: any) {
      this.scheduleStatus = 'Error: ' + e.message;
    } finally {
      this.isSavingSchedule = false;
    }
  }
}
